import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Pencil, CheckCircle2, Undo2, EyeOff, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { ArticleView } from "@/pages/ArticlePage";
import { PageState } from "@/pages/PageState";
import { StatusBadge } from "@/pages/ContributePage";

const Btn = ({ icon: Icon, children, ...p }) => (
  <button {...p} className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e6bb45] font-heading text-xs font-bold uppercase tracking-wider hover:bg-[#e6bb45]/20 transition-colors"><Icon className="w-4 h-4" /> {children}</button>
);

const Banner = ({ a, isAdmin, reload }) => {
  const { refresh } = useSite();
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const isSubmission = !!a.review_status;
  const status = isSubmission ? a.review_status : a.published ? "approved" : "draft";
  return (
    <div data-testid="preview-banner" className="sticky top-[84px] z-30 bg-[#1a120c] text-[#f4efe2] border-y-2 border-[#c9a227]">
      <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
        <span className="font-heading text-sm font-bold">Preview</span>
        <span className="bg-[#f4efe2] px-1"><StatusBadge status={status} /></span>
        <span className="text-sm italic opacity-80 mr-auto">{a.published ? "This page is live for readers." : "Only you can see this page."}{a.author_name && ` · by ${a.author_name}`}</span>
        <Link to={isAdmin ? `/admin/articles/${a.id}` : `/contribute/${a.id}`} data-testid="preview-edit-link" className="inline-flex items-center gap-1.5 px-4 py-2 border border-[#e6bb45] font-heading text-xs font-bold uppercase tracking-wider hover:bg-[#e6bb45]/20"><Pencil className="w-4 h-4" /> Edit</Link>
        {isAdmin && isSubmission && a.review_status !== "approved" && <>
          <Btn icon={CheckCircle2} data-testid="preview-approve" onClick={() => act(() => api.post(`/admin/submissions/${a.id}/approve`), "Approved & published")}>Approve & publish</Btn>
          {a.review_status === "pending" && <Btn icon={Undo2} data-testid="preview-reject" onClick={() => { const note = window.prompt("Note to the author (optional)") ?? null; if (note !== null) act(() => api.post(`/admin/submissions/${a.id}/reject`, { note }), "Returned to author"); }}>Return</Btn>}
        </>}
        {isAdmin && (!isSubmission || a.review_status === "approved") && (a.published
          ? <Btn icon={EyeOff} data-testid="preview-unpublish" onClick={() => act(() => api.post(`/admin/articles/${a.id}/publish`, { published: false }), "Unpublished")}>Unpublish</Btn>
          : <Btn icon={Send} data-testid="preview-publish" onClick={() => act(() => api.post(`/admin/articles/${a.id}/publish`, { published: true }), "Published")}>Publish now</Btn>)}
      </div>
    </div>
  );
};

export default function PreviewPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(undefined);
  const load = useCallback(() => api.get(`/preview/articles/${id}`).then((r) => setA(r.data)).catch(() => setA(null)), [id]);
  useEffect(() => { if (user) load(); else if (user === false) setA(null); }, [user, load]);
  if (!a) return <PageState loading={a === undefined} />;
  return <ArticleView a={a} banner={<Banner a={a} isAdmin={user?.role === "admin"} reload={load} />} />;
}
